/* AvatarCropDialog — minimal canvas-based square crop + zoom flow.

   Sits between "picked a file" and "uploaded". The studio drags / picks
   any photo on /admin/profile; instead of shipping it straight to object
   storage we open this dialog so they can:

     • zoom (range slider, 1.0–3.0×)
     • pan (drag the image inside the square viewport)
     • see the final circular crop live
     • confirm → we draw the crop to a 512×512 canvas, hand the parent the
       resulting JPEG Blob via `onConfirm(blob, filename)`. The parent then
       POSTs the blob to the existing /admin/uploads endpoint as before.

   We avoid adding a crop library — this whole component is ~150 lines of
   plain React + canvas, matches the editorial chrome exactly, and stays
   pleasant on touch (pointer events handle mouse + touch the same way).

   Hard limit: the viewport stays a fixed 320×320 square (we render the
   final crop at 512×512 — high enough for the 80px Identity preview and
   the 32px header avatar to read crisply on retina screens). */

import { useEffect, useMemo, useRef, useState } from "react";
import { X, Loader2, Move, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

const VIEWPORT = 320;       // square crop window (CSS px)
const OUTPUT_SIZE = 512;    // final JPEG side (canvas px)
const MIN_SCALE = 1;
const MAX_SCALE = 3;

export default function AvatarCropDialog({ file, onConfirm, onCancel }) {
  const [dataUrl, setDataUrl] = useState("");
  const [img, setImg] = useState(null);              // HTMLImageElement once loaded
  const [scale, setScale] = useState(1);
  // Offset in screen pixels — translation applied AFTER scaling. We clamp
  // it so the studio can't accidentally drag the image off the viewport.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [exporting, setExporting] = useState(false);

  const dragRef = useRef(null);     // { startX, startY, baseX, baseY } while dragging
  const viewportRef = useRef(null); // for non-passive wheel listener (see below)

  // 1. Read the file → data URL → load into an HTMLImageElement so we know
  //    its natural dimensions before computing the initial "cover" scale.
  useEffect(() => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setDataUrl(String(e.target?.result || ""));
    reader.readAsDataURL(file);
  }, [file]);

  useEffect(() => {
    if (!dataUrl) return;
    const i = new Image();
    i.onload = () => {
      setImg(i);
      // Reset transform whenever a new image lands. Center on the image.
      setScale(1);
      setOffset({ x: 0, y: 0 });
    };
    i.src = dataUrl;
  }, [dataUrl]);

  // 2. "Cover" sizing — the rendered image is at LEAST VIEWPORT on both
  //    sides when scale=1, like CSS object-fit: cover. Studios can then
  //    zoom in further (scale > 1) for a tighter crop.
  const renderSize = useMemo(() => {
    if (!img) return { w: VIEWPORT, h: VIEWPORT };
    const ratio = img.naturalWidth / img.naturalHeight;
    if (ratio >= 1) {
      // landscape — height matches viewport
      return { w: VIEWPORT * ratio, h: VIEWPORT };
    }
    // portrait
    return { w: VIEWPORT, h: VIEWPORT / ratio };
  }, [img]);

  // 3. Pan limits — image edges can't pull inside the viewport edges.
  function clamp(off, s = scale) {
    const renderedW = renderSize.w * s;
    const renderedH = renderSize.h * s;
    const limX = Math.max(0, (renderedW - VIEWPORT) / 2);
    const limY = Math.max(0, (renderedH - VIEWPORT) / 2);
    return {
      x: Math.max(-limX, Math.min(limX, off.x)),
      y: Math.max(-limY, Math.min(limY, off.y)),
    };
  }

  // Re-clamp offset when scale changes so the image can never pop a corner
  // outside the viewport after zooming back out.
  useEffect(() => { setOffset((o) => clamp(o, scale)); }, [scale, renderSize.w, renderSize.h, clamp]);

  // 4. Pointer drag
  function onPointerDown(e) {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: offset.x, baseY: offset.y };
  }
  function onPointerMove(e) {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(clamp({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy }));
  }
  function onPointerUp() { dragRef.current = null; }

  // Mouse-wheel / trackpad zoom — pairs with the slider so power users
  // can zoom from the viewport itself. Attached via a non-passive listener
  // (React's synthetic onWheel is passive, so preventDefault would be a
  // no-op there). preventDefault stops the page from scrolling while the
  // cursor is over the crop area. Small exponential factor keeps the
  // mouse wheel and trackpad feeling even (a linear delta makes trackpads
  // way too sensitive).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    function onWheel(e) {
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * 0.0015);
      setScale((s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s * factor)));
    }
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Snap back to the initial frame — useful when the studio has zoomed +
  // panned and wants to start over without re-picking the file.
  function resetCrop() {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }

  // 5. Export — draw to OUTPUT_SIZE canvas at the right transform, then
  //    canvas.toBlob → parent handles the upload.
  async function handleConfirm() {
    if (!img) return;
    setExporting(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0e0d0c";
      ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

      // Translate viewport coords → canvas coords. CSS scales the image
      // by VIEWPORT-based math; multiply through by (OUTPUT_SIZE / VIEWPORT).
      const k = OUTPUT_SIZE / VIEWPORT;
      const drawW = renderSize.w * scale * k;
      const drawH = renderSize.h * scale * k;
      const drawX = (OUTPUT_SIZE - drawW) / 2 + offset.x * k;
      const drawY = (OUTPUT_SIZE - drawH) / 2 + offset.y * k;
      ctx.drawImage(img, drawX, drawY, drawW, drawH);

      const blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92),
      );
      if (!blob) throw new Error("Couldn't render crop");
      const ext = (file?.name?.split(".").pop() || "jpg").toLowerCase();
      const baseName = (file?.name?.replace(/\.[^.]+$/, "") || "avatar");
      onConfirm(blob, `${baseName}-${OUTPUT_SIZE}.jpg`, ext);
    } finally {
      setExporting(false);
    }
  }

  // 6. Esc closes; trap focus minimally (the confirm button auto-focuses).
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !exporting) onCancel(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, exporting]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Crop your avatar"
      data-testid="avatar-crop-dialog"
    >
      <div className="w-full max-w-md border border-border bg-card rounded-sm shadow-2xl shadow-black/50 overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--gold))]">
              Crop your avatar
            </div>
            <div className="text-base font-serif text-foreground mt-0.5">Frame the moment.</div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={exporting}
            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
            aria-label="Cancel"
            data-testid="avatar-crop-cancel-icon"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-5">
          {/* Viewport — drag-to-pan square with a circular mask overlay. */}
          <div
            ref={viewportRef}
            className="relative mx-auto overflow-hidden select-none bg-[#0e0d0c] cursor-grab active:cursor-grabbing"
            style={{ width: VIEWPORT, height: VIEWPORT, touchAction: "none" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            data-testid="avatar-crop-viewport"
          >
            {img ? (
              <img
                src={dataUrl}
                alt=""
                draggable={false}
                className="absolute pointer-events-none"
                style={{
                  width: renderSize.w,
                  height: renderSize.h,
                  left: "50%",
                  top: "50%",
                  transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                  transformOrigin: "center center",
                }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            )}
            {/* Circular dim mask via radial-gradient — keeps the preview round
                while leaving the corners faintly visible so the studio sees
                what they're excluding. */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at center, transparent 0, transparent calc(50% - 1px), rgba(14,13,12,0.55) 50%)",
              }}
            />
            <div
              className="absolute inset-0 pointer-events-none border-2 rounded-full"
              style={{ borderColor: "hsl(var(--gold))" }}
            />
            <div className="absolute left-3 bottom-3 inline-flex items-center gap-1.5 px-2 h-6 text-[9px] uppercase tracking-[0.32em] bg-black/60 text-white/90 pointer-events-none">
              <Move className="w-3 h-3" /> Drag · scroll to zoom
            </div>
          </div>

          {/* Zoom slider — flanked by min/max icons so it reads without copy. */}
          <div className="flex items-center gap-3">
            <ZoomOut className="w-4 h-4 text-muted-foreground" />
            <input
              type="range"
              min={MIN_SCALE}
              max={MAX_SCALE}
              step={0.01}
              value={scale}
              onChange={(e) => setScale(parseFloat(e.target.value))}
              className="flex-1 accent-[hsl(var(--gold))]"
              data-testid="avatar-crop-zoom"
              aria-label="Zoom"
            />
            <ZoomIn className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>

        <footer className="px-5 py-4 border-t border-border flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={resetCrop}
            disabled={exporting || (scale === 1 && offset.x === 0 && offset.y === 0)}
            className="inline-flex items-center gap-1.5 px-3 h-9 text-[10px] uppercase tracking-[0.28em] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="avatar-crop-reset"
            title="Reset to original framing"
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
          <div className="inline-flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={exporting}
              className="px-4 h-10 text-[10px] uppercase tracking-[0.32em] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
              data-testid="avatar-crop-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!img || exporting}
              autoFocus
              className="inline-flex items-center gap-2 px-5 h-10 bg-[hsl(var(--gold))] text-background text-[10px] uppercase tracking-[0.32em] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
              data-testid="avatar-crop-confirm"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {exporting ? "Cropping…" : "Crop & upload"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
